import React, { useState, useMemo } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { useTenant } from '@/contexts/TenantContext';
import { useTheme } from '@/contexts/ThemeContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import TenantSelector from '@/components/admin/TenantSelector';
import {
  LayoutDashboard,
  FileText,
  Users,
  MapPin,
  Clock,
  CalendarDays,
  Settings,
  LogOut,
  Menu,
  X,
  Globe,
  Shield,
  UserCog,
  Building2,
  List,
  Calculator,
  User,
  BarChart3,
  Moon,
  Sun,
  BookOpen,
  GraduationCap,
  Banknote
} from 'lucide-react';
import GuidedTour, { restartTour } from '@/components/admin/GuidedTour';

export default function DashboardLayout() {
  const { t, language, toggleLanguage } = useLanguage();
  const { user, logout } = useAuth();
  const { isPlatformAdmin } = useTenant();
  const { isDark, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Contextual help mapping: current path -> documentation section
  const helpLink = useMemo(() => {
    const path = location.pathname.replace('/admin/', '').replace('/admin', '');
    const map = {
      'dashboard': '?guide=rh-paie&section=tableau-de-bord&sub=comprendre-dashboard',
      '': '?guide=rh-paie&section=tableau-de-bord&sub=comprendre-dashboard',
      'reports': '?guide=rh-paie&section=rapports&sub=consulter-rapports',
      'employees': '?guide=rh-paie&section=gestion-employes&sub=liste-employes',
      'shifts': '?guide=rh-paie&section=quarts-travail&sub=gerer-quarts',
      'scheduling': '?guide=rh-paie&section=planification&sub=vue-planification',
      'sites-zones': '?guide=rh-paie&section=sites-zones&sub=gerer-sites',
      'payroll': '?guide=rh-paie&section=module-paie&sub=periodes-paie',
      'analytics': '?guide=rh-paie&section=analytique&sub=resume-executif',
      'lookups': '?guide=rh-paie&section=listes-reference&sub=gerer-lookups',
      'settings': '?guide=rh-paie&section=parametres&sub=parametres-generaux',
      'users': '?guide=rh-paie&section=gestion-employes&sub=liste-employes',
      'platform': '?guide=super-admin&section=supervision&sub=kpis-globaux',
    };
    return map[path] || '';
  }, [location.pathname]);

  const handleExitSupport = async () => {
    const originalToken = localStorage.getItem('punchtm-original-token');
    const interventionId = localStorage.getItem('punchtm-support-intervention');
    if (interventionId) {
      try {
        const { API_URL } = require('@/utils/api');
        const axios = require('axios').default;
        await axios.post(`${API_URL}/platform/support/exit?interventionId=${interventionId}`, {}, {
          headers: { Authorization: `Bearer ${originalToken}` }
        });
      } catch (e) { console.error('Failed to log support exit:', e); }
    }
    if (originalToken) {
      localStorage.setItem('punchtm-token', originalToken);
    }
    localStorage.removeItem('punchtm-original-token');
    localStorage.removeItem('punchtm-support-intervention');
    localStorage.removeItem('punchtm-support-mode');
    localStorage.removeItem('punchtm-support-tenant');
    window.location.href = '/admin/platform';
  };

  const handleLogout = () => {
    const originalToken = localStorage.getItem('punchtm-original-token');
    if (originalToken) {
      handleExitSupport();
      return;
    }
    logout();
    navigate('/admin/login');
  };

  // Role-based navigation items
  const getNavItems = () => {
    const role = user?.role;
    const items = [];
    
    // All roles (except employee) see dashboard
    if (role !== 'employee') {
      items.push({ path: '/admin/dashboard', icon: LayoutDashboard, label: t('dashboard') });
    }
    
    // Reports - all admin roles (including legacy 'admin', supervisor, and HR)
    if (['platform_superadmin', 'tenant_superuser', 'tenant_hr', 'tenant_admin', 'tenant_supervisor', 'admin'].includes(role)) {
      items.push({ path: '/admin/reports', icon: FileText, label: t('reports') });
    }
    
    // Employees - admin and above + supervisor + HR (HR needs full visibility)
    if (['platform_superadmin', 'tenant_superuser', 'tenant_hr', 'tenant_admin', 'tenant_supervisor', 'admin'].includes(role)) {
      items.push({ path: '/admin/employees', icon: Users, label: t('employees') });
    }
    
    // Shifts - admin and above + supervisor + HR (read-only for supervisor/HR)
    if (['platform_superadmin', 'tenant_superuser', 'tenant_hr', 'tenant_admin', 'tenant_supervisor', 'admin'].includes(role)) {
      items.push({ path: '/admin/shifts', icon: Clock, label: language === 'fr' ? 'Quarts' : 'Shifts' });
    }
    
    // Scheduling - admin and above + supervisor + HR
    if (['platform_superadmin', 'tenant_superuser', 'tenant_hr', 'tenant_admin', 'tenant_supervisor', 'admin'].includes(role)) {
      items.push({ path: '/admin/scheduling', icon: CalendarDays, label: language === 'fr' ? 'Planification' : 'Scheduling' });
    }
    
    // Sites/Zones - admin and above + HR (HR needs to see site structure)
    if (['platform_superadmin', 'tenant_superuser', 'tenant_hr', 'tenant_admin', 'admin'].includes(role)) {
      items.push({ path: '/admin/sites-zones', icon: MapPin, label: t('sitesZones') });
    }
    
    // Users management - superuser and above + HR (HR can view but not manage)
    if (['platform_superadmin', 'tenant_superuser', 'tenant_hr'].includes(role)) {
      items.push({ path: '/admin/users', icon: UserCog, label: language === 'fr' ? 'Utilisateurs' : 'Users' });
    }
    
    // Lookup tables (reference data) - admin and above + HR
    if (['platform_superadmin', 'tenant_superuser', 'tenant_hr', 'tenant_admin', 'admin'].includes(role)) {
      items.push({ path: '/admin/lookups', icon: List, label: language === 'fr' ? 'Listes' : 'Lists' });
    }
    
    // Payroll / HR Module - HR, superuser, platform admin only (not supervisors)
    if (['platform_superadmin', 'tenant_superuser', 'tenant_hr', 'admin'].includes(role)) {
      items.push({ path: '/admin/payroll', icon: Calculator, label: language === 'fr' ? 'Paie / RH' : 'Payroll / HR' });
    }

    // PaieRH Africa - Senegal payroll module (HR, superuser, platform admin)
    if (['platform_superadmin', 'tenant_superuser', 'tenant_hr', 'admin'].includes(role)) {
      items.push({ path: '/admin/paierh', icon: Banknote, label: 'PaieRH Africa' });
    }

    // Analytics - HR, superuser, platform admin
    if (['platform_superadmin', 'tenant_superuser', 'tenant_hr', 'admin'].includes(role)) {
      items.push({ path: '/admin/analytics', icon: BarChart3, label: language === 'fr' ? 'Analytique' : 'Analytics' });
    }
    
    // Settings - admin and above + HR (HR needs to configure payroll settings)
    if (['platform_superadmin', 'tenant_superuser', 'tenant_hr', 'tenant_admin', 'admin'].includes(role)) {
      items.push({ path: '/admin/settings', icon: Settings, label: t('settings') });
    }
    
    // Platform admin - only for platform_superadmin
    if (role === 'platform_superadmin') {
      items.push({ path: '/admin/platform', icon: Building2, label: language === 'fr' ? 'Plateforme' : 'Platform' });
    }

    // Documentation - all roles
    items.push({ path: '/admin/documentation', icon: BookOpen, label: 'Documentation' });
    
    return items;
  };

  const navItems = getNavItems();

  // Role badge config
  const getRoleBadge = () => {
    const role = user?.role;
    const config = {
      platform_superadmin: { label: 'Platform Admin', color: 'bg-purple-100 text-purple-700' },
      tenant_superuser: { label: 'Superuser', color: 'bg-red-100 text-red-700' },
      tenant_hr: { label: 'RH/Paie', color: 'bg-pink-100 text-pink-700' },
      tenant_admin: { label: 'Admin', color: 'bg-blue-100 text-blue-700' },
      admin: { label: 'Admin', color: 'bg-blue-100 text-blue-700' },  // Legacy admin role
      tenant_supervisor: { label: 'Supervisor', color: 'bg-green-100 text-green-700' },
      employee: { label: 'Employee', color: 'bg-slate-100 text-slate-700' }
    };
    return config[role] || { label: role, color: 'bg-slate-100 text-slate-700' };
  };

  const roleBadge = getRoleBadge();
  const isImpersonating = localStorage.getItem('punchtm-original-token');
  const isSupportMode = localStorage.getItem('punchtm-support-mode') === 'true';
  const supportTenant = localStorage.getItem('punchtm-support-tenant');

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 font-body transition-colors duration-200" data-testid="dashboard-layout">
      {/* Support Mode Banner */}
      {isImpersonating && isSupportMode && (
        <div className="fixed top-0 left-0 right-0 z-50 bg-blue-600 text-white py-2 px-4 flex items-center justify-center gap-3 text-sm" data-testid="support-mode-banner">
          <Shield size={16} />
          <span>
            <strong>Mode Support</strong> — Intervention sur <strong>{supportTenant || 'tenant'}</strong>
          </span>
          <button
            onClick={handleExitSupport}
            className="ml-3 bg-white/20 hover:bg-white/30 text-white px-3 py-1 rounded text-xs font-medium transition-colors"
            data-testid="exit-support-btn"
          >
            Quitter le mode support
          </button>
        </div>
      )}
      {/* Impersonation Banner (non-support) */}
      {isImpersonating && !isSupportMode && (
        <div className="fixed top-0 left-0 right-0 z-50 bg-amber-500 text-white py-2 px-4 text-center text-sm">
          <Shield size={16} className="inline mr-2" />
          {language === 'fr'
            ? `Vous êtes connecté en tant que ${user?.firstName} ${user?.lastName}. `
            : `You are logged in as ${user?.firstName} ${user?.lastName}. `}
          <button
            onClick={handleLogout}
            className="underline font-medium hover:text-amber-100"
          >
            {language === 'fr' ? 'Revenir à mon compte' : 'Return to my account'}
          </button>
        </div>
      )}

      {/* Mobile Header */}
      <header className={`lg:hidden fixed left-0 right-0 z-40 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 px-4 py-3 ${isImpersonating ? 'top-10' : 'top-0'}`}>
        <div className="flex items-center justify-between">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-900 dark:text-slate-100"
            data-testid="mobile-menu-toggle"
          >
            {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
          
          <div className="flex items-center gap-2">
            <img src="/logo-punchtm.png" alt="PunchTM" className="h-8 w-auto" />
          </div>
          
          <div className="flex items-center gap-1">
            <button
              onClick={toggleTheme}
              className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-300"
              data-testid="theme-toggle-mobile"
            >
              {isDark ? <Sun size={20} /> : <Moon size={20} />}
            </button>
            <button
              onClick={toggleLanguage}
              className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800"
              data-testid="language-toggle-mobile"
            >
              <Globe size={20} className="text-slate-600 dark:text-slate-300" />
            </button>
          </div>
        </div>
      </header>

      {/* Sidebar */}
      <aside
        className={`
          fixed left-0 z-30 h-full w-64 bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-800
          transform transition-transform duration-300 ease-in-out
          lg:translate-x-0
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
          ${isImpersonating ? 'top-10' : 'top-0'}
        `}
        data-testid="sidebar"
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-5 border-b border-slate-200 dark:border-slate-800">
            <div className="flex items-center justify-center">
              <img src="/logo-punchtm.png" alt="PunchTM" className="h-10 w-auto" />
            </div>
          </div>

          {/* Tenant Selector for Platform Admin */}
          {isPlatformAdmin && (
            <div className="p-4 border-b border-slate-200 dark:border-slate-800 bg-purple-50/50 dark:bg-purple-950/30">
              <TenantSelector />
            </div>
          )}

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {navItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-3 rounded-lg transition-all font-medium
                  ${isActive 
                    ? 'bg-primary text-white dark:bg-slate-700 dark:text-white' 
                    : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-primary dark:hover:text-white'
                  }`
                }
                data-testid={`nav-${item.path.split('/').pop()}`}
              >
                <item.icon size={20} />
                <span>{item.label}</span>
              </NavLink>
            ))}
          </nav>

          {/* Kiosk Link */}
          <div className="p-4 border-t border-slate-200 dark:border-slate-800">
            <NavLink
              to="/kiosk"
              className="flex items-center gap-3 px-4 py-3 rounded-lg bg-accent/10 text-accent hover:bg-accent/20 transition-all font-medium"
              data-testid="nav-kiosk"
            >
              <Clock size={20} />
              <span>{t('kiosk')}</span>
            </NavLink>
          </div>

          {/* User Info & Actions */}
          <div className="p-4 border-t border-slate-200 dark:border-slate-800">
            <button
              onClick={() => { navigate('/admin/profile'); setSidebarOpen(false); }}
              className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors mb-2"
              data-testid="nav-profile"
            >
              <div className="w-8 h-8 bg-primary/10 dark:bg-slate-700 rounded-full flex items-center justify-center">
                <User size={16} className="text-primary dark:text-slate-300" />
              </div>
              <div className="flex-1 min-w-0 text-left">
                <p className="font-medium text-sm truncate text-slate-900 dark:text-slate-100">{user?.firstName} {user?.lastName}</p>
                <p className="text-xs text-slate-500 dark:text-slate-400 truncate">{user?.email}</p>
              </div>
            </button>
            
            <Badge className={`${roleBadge.color} mb-3 w-full justify-center`}>
              {roleBadge.label}
            </Badge>

            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                className="flex-1 justify-center gap-1"
                onClick={handleLogout}
                data-testid="logout-btn"
              >
                <LogOut size={16} />
                {isImpersonating 
                  ? (isSupportMode 
                    ? (language === 'fr' ? 'Quitter support' : 'Exit support')
                    : (language === 'fr' ? 'Revenir' : 'Return'))
                  : (language === 'fr' ? 'Deconnexion' : 'Sign Out')}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={toggleTheme}
                data-testid="theme-toggle"
                title={isDark ? 'Mode clair' : 'Mode sombre'}
              >
                {isDark ? <Sun size={16} /> : <Moon size={16} />}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={toggleLanguage}
                data-testid="language-toggle"
              >
                <span className="text-xs font-bold uppercase">{language}</span>
              </Button>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="w-full mt-2 justify-center gap-1.5 text-xs text-slate-400 hover:text-primary"
              onClick={restartTour}
              data-testid="restart-tour-btn"
            >
              <GraduationCap size={14} />
              {language === 'fr' ? 'Visite guidee' : 'Guided tour'}
            </Button>
          </div>
        </div>
      </aside>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 z-20 bg-black/50"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <main className={`lg:ml-64 min-h-screen ${isImpersonating ? 'pt-26 lg:pt-10' : 'pt-16 lg:pt-0'}`}>
        <div className="p-6 lg:p-8">
          <Outlet />
        </div>
      </main>

      {/* Floating Contextual Help Button */}
      {helpLink && !location.pathname.includes('/documentation') && (
        <button
          onClick={() => navigate(`/admin/documentation${helpLink}`)}
          className="fixed bottom-6 right-6 z-40 w-11 h-11 rounded-full bg-primary text-white shadow-lg hover:shadow-xl hover:scale-105 transition-all flex items-center justify-center group"
          title={language === 'fr' ? 'Aide contextuelle' : 'Contextual help'}
          data-testid="contextual-help-fab"
        >
          <BookOpen size={18} />
          <span className="absolute right-full mr-2 px-2 py-1 text-xs font-medium bg-slate-900 text-white rounded-md whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
            {language === 'fr' ? 'Aide' : 'Help'}
          </span>
        </button>
      )}

      {/* Guided Tour */}
      <GuidedTour />
    </div>
  );
}
